import { Progress, Upload, message } from "antd";
import { Dispatch, ReactNode, SetStateAction } from "react";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { RcFile, UploadProps } from "antd/lib/upload";
import "./style.scss";
import { customRequestUpload, translate } from "utils";
import { t } from "@lingui/macro";

const UploadVideo = ({
	video,
	onAdd,
	onDelete,
	maxSizeVideo,
	loading,
	setLoading,
	progressVideo,
	errorMessage,
	maximumDesc,
	borderClassName,
	labelClassName,
	removeIcon,
	removeClassName,
	videoClassName,
	uploadCustomElement,
	disabled
}: {
	video: string;
	onAdd: (file: RcFile) => void;
	onDelete: () => void;
	maxSizeVideo: number;
	loading: boolean;
	setLoading: Dispatch<SetStateAction<boolean>>;
	progressVideo: number;
	errorMessage?: string;
	maximumDesc?: string;
	borderClassName?: string;
	labelClassName?: string;
	removeIcon?: ReactNode;
	removeClassName?: string;
	videoClassName?: string;
	uploadCustomElement?: ReactNode;
	disabled?: boolean;
}) => {
	const handleChange: UploadProps["onChange"] = info => {
		if (info.file.status === "uploading") {
			setLoading(true);
		} else {
			onAdd(info.file.originFileObj as RcFile);
		}
	};

	const beforeUploadVideo = (file: RcFile) => {
		const isVideo =
			file.type === "video/quicktime" || file.type === "video/mp4";
		if (!isVideo) {
			message.error(translate("Video must be MP4 or MOV"));
			return Upload.LIST_IGNORE;
		}
		if (file.size > maxSizeVideo) {
			message.error(translate("Video size must under 99MB"));
			return Upload.LIST_IGNORE;
		}
	};

	return (
		<div
			className={`${borderClassName ?? "border mt-10 py-2 flex flex-col items-center xl:mx-[6.75rem]"} ${
				errorMessage ? "border-[#ff4d4f]" : "border-slate-500"
			}`}
		>
			{video ? (
				<div className="w-1/2 relative">
					<p className={`${labelClassName ?? "text-base font-medium flex justify-start"}`}>{t`Video`}</p>
					<video
						className={videoClassName ?? "block w-full h-full max-h-[400px]"}
						controls
						src={video}
					/>
					<div
						onClick={() =>
							window.dialogConfirm(
								translate("Confirm remove file"),
								translate("Are you sure to remove this file?"),
								(confirm: boolean) => {
									if (confirm) {
										onDelete();
									}
								},
							)
						}
						className={`${
							removeClassName
							??
							"bg-slate-300 bg-opacity-40 hover:bg-opacity-100 absolute items-center px-2 py-1 right-2 top-2 rounded-full hover:cursor-pointer"
						}`}
					>
						{
							removeIcon
							??
							<CloseOutlined className="text-lg text-red-500 mt-[-6px]" />
						}
					</div>
				</div>
			) : (
				<div className="upload-video-wrapper">
					<p className={labelClassName ?? "text-base font-medium flex justify-start"}>{t`Video`}</p>
					<Upload
						name="uploadVideo"
						listType="picture-card"
						showUploadList={false}
						beforeUpload={beforeUploadVideo}
						onChange={handleChange}
						accept=".mov, .mp4"
						customRequest={customRequestUpload}
						disabled={disabled}
					>
						{loading ? (
							<Progress
								type="circle"
								percent={progressVideo}
								className="progressUpload"
								strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
							/>
						) : (
							uploadCustomElement
							??
							<div>
								<PlusOutlined className="text-xl" />
								<p>{t`Upload`}</p>
								{maximumDesc && (
									<p className="text-xs italic">{`(${translate(
										maximumDesc
									)})`}</p>
								)}
							</div>
						)}
					</Upload>
				</div>
			)}

			{errorMessage && <p className="text-[#ff4d4f]">{translate(errorMessage)}</p>}
		</div>
	);
};

export default UploadVideo;
